// ---------------------------------------------------------------------------
// The current order.
//
// Each line shows what a customer would ask about: the product, how much of
// it, the price it is being charged at, and the line total. Weights are always
// shown to three decimals, so 1.250 kg never appears as 1.25.
//
// Payment is not here. It is built in Phase 3, and the panel says so rather
// than offering a button that would do nothing.
//
// LIRA IS THE AMOUNT; DOLLARS ARE THE WORKING. The shop prices in dollars and
// is PAID in lira, so the big figure on every line is the lira one - it is
// what the cashier says out loud and the customer counts out.
//
// Under it sits ONE row that shows the arithmetic in the currency the price
// was set in:
//
//     1,920,312 LBP
//     1.2 kg × $17.88/kg                              $21.46
//
// One line, not four. The weight and the dollar unit price on the near side,
// the dollar line total on the far side, and no repetition of either figure.
// The lira unit price is deliberately absent: multiplying 1,600,260 by 1.2 in
// your head is not something anybody does mid-queue, and the lira total is
// already printed above.
//
// THE DELIVERY FEE IS LIRA ONLY - typed in lira, shown in lira, with no
// currency choice and no dollar echo. It is the one figure the shop quotes to
// a customer directly.
//
// THE RECEIPT IS DIFFERENT ON PURPOSE and keeps its own layout; that contrast
// is pinned by tests in both directions.
//
// THE LIRA ARE NOT CONVERTED FROM THE DOLLARS. They are worked out from the
// lira prices the shop set, and the dollars beside them are what those come to
// at the header's rate. It used to be the other way round, and a line priced
// at 300,000 was shown - and sold - as 299,825, because a lira figure does not
// fit in a dollar column. With no rate set there is nothing to price in lira
// with, so the dollar figures stand in and the panel says why.
// ---------------------------------------------------------------------------

import type { ReactNode } from 'react';
import { CreditCard, Flame, LockKeyhole, PauseCircle, Pencil, ShoppingCart, StickyNote, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '../../i18n/I18nProvider';
import {
  formatUsd,
  formatWeight,
  LBP_LABEL,
  toDecimal,
  unitLabel,
  formatLbp,
} from '../../utils/format';
import { optionLine } from '../../utils/optionLabel';
import {
  cartSubtotal,
  cartSubtotalLbp,
  lineTotal,
  lineTotalLbp,
  type CartLine,
} from '../../stores/cartStore';
import { EmptyState } from '../common/StateBlocks';
import { LbpMoneyInput } from '../common/LbpMoneyInput';

interface CartPanelProps {
  lines: CartLine[];
  orderNote: string;
  /// The current USD to LBP rate, or null when none has been set.
  exchangeRate: string | null;
  /// False when the cashier has no open shift. The button is then disabled,
  /// because the server would refuse the sale anyway.
  canCheckout: boolean;
  onEditLine: (line: CartLine) => void;
  onRemoveLine: (line: CartLine) => void;
  onOrderNoteChange: (note: string) => void;
  onClear: () => void;
  onCheckout: () => void;

  // --- delivery mode -------------------------------------------------------
  /// The delivery fee in dollars. Shown as its own line and added to the
  /// total, never as a cart line - it moves no stock and has no category.
  deliveryFeeUsd?: string;
  /// The same fee in LIRA, which is what it was typed in. Used for the totals
  /// this panel shows, so the figure the cashier reads out is the one the
  /// server will charge - the dollar version rounds to the cent and, converted
  /// back, turns 100,000 into 100,250.
  deliveryFeeLbp?: string;
  /// The existing order's authoritative dollar total (avoids adding rounded lines).
  frozenTotalUsd?: string;
  showDeliveryFee?: boolean;
  /// Lets the cashier type a delivery or transport charge onto a COUNTER
  /// sale, without a customer, an address or a delivery order.
  ///
  /// Absent in delivery mode, where the fee belongs to the order and is
  /// frozen at the rate the customer was quoted - editing it here would put
  /// two different numbers on one piece of paper.
  feeEditor?: {
    /// The amount as typed, IN LIRA. There is no currency to choose: see the
    /// field itself for why.
    amount: string;
    onAmountChange: (amount: string) => void;
    /// Shown under the field. The fee is refused, not silently clamped.
    problem?: string | null;
    /// The shop's standing delivery fee, offered as one tap while the field
    /// is empty. A positive fee is what MAKES an order a delivery, so this
    /// chip is the fastest way from a built cart to a dispatched driver.
    quickFill?: { label: string; onFill: () => void } | null;
    confirmation?: { onConfirm: () => void; disabled: boolean; busy: boolean };
  };
  /// Given only to somebody with `held_orders.create`. Absent, no button.
  onHold?: () => void;
  /// Anything that goes above the pay button - in practice the "send to
  /// preparation" button, which only exists in delivery mode.
  footerExtra?: ReactNode;
  context?: ReactNode;
  onSetAside?: () => void;
  setAsideDisabled?: boolean;
  /// Overrides "Take payment" when the sale is settling an existing order.
  checkoutLabel?: string;
  /// Blocks checkout with a reason the cashier can act on, shown above the
  /// buttons rather than as a silently disabled control.
  blockedReason?: string | null;
  /// A FROZEN order under review (an online menu order): no editing, no
  /// removing, clearing, holding or note editing. Settlement remains manual.
  readOnly?: boolean;

  /// How this panel is allowed to use the space it is given.
  ///
  ///   'fill'    it owns a column: it fills the height, the item list scrolls
  ///             inside it, and the totals sit at the bottom of the screen.
  ///   'natural' it is inside something that already scrolls - the narrow
  ///             screen drawer. It takes its natural height and the totals
  ///             stick to the bottom of the viewport as the drawer scrolls,
  ///             so Take Payment is reachable without scrolling to the end.
  ///
  /// One panel, two containers. A second implementation for the drawer is how
  /// a delivery field ends up on the desktop and missing on the tablet.
  flow?: 'fill' | 'natural';
}

export function CartPanel({
  lines,
  orderNote,
  exchangeRate,
  deliveryFeeLbp,
  canCheckout,
  onEditLine,
  onRemoveLine,
  onOrderNoteChange,
  onClear,
  onCheckout,
  deliveryFeeUsd,
  frozenTotalUsd,
  showDeliveryFee = false,
  feeEditor,
  onHold,
  footerExtra,
  context,
  onSetAside,
  setAsideDisabled,
  checkoutLabel,
  blockedReason,
  readOnly = false,
  flow = 'fill',
}: CartPanelProps) {
  const { t, locale, format } = useI18n();

  const itemsSubtotal = cartSubtotal(lines, exchangeRate);
  const fee = toDecimal(deliveryFeeUsd ?? '0');
  const hasFee = fee.greaterThan(0);
  // What the customer actually pays. Every figure below is Decimal to the end.
  const subtotal = frozenTotalUsd !== undefined ? toDecimal(frozenTotalUsd) : itemsSubtotal.plus(fee).toDecimalPlaces(2);

  // ---- and the same three figures in LIRA, which are the authoritative ones -
  //
  // Worked out FROM the lira prices, not by converting the dollar figures
  // above. That conversion is precisely what turned a 300,000 line into
  // 299,825 on the shop's screen, and the cashier reads these numbers out.
  const itemsSubtotalLbp = exchangeRate ? cartSubtotalLbp(lines, exchangeRate) : null;
  // The fee in lira. Given directly when it was typed in lira - which is how
  // the till takes it - and converted from the dollar figure otherwise, which
  // is the case for a delivery order whose fee was frozen in dollars when the
  // customer was quoted. Falling back to zero would show a fee of nothing on a
  // sale that charges one.
  const feeLbp =
    deliveryFeeLbp !== undefined
      ? toDecimal(deliveryFeeLbp)
      : toDecimal(deliveryFeeUsd ?? '0').times(toDecimal(exchangeRate ?? '0')).toDecimalPlaces(0);
  const subtotalLbpExact = itemsSubtotalLbp ? itemsSubtotalLbp.plus(feeLbp) : null;

  /// A lira figure this panel worked out, formatted - or null with no rate.
  const exact = (value: ReturnType<typeof toDecimal> | null) =>
    value === null ? null : formatLbp(value, locale);
  const empty = lines.length === 0;

  return (
    <section
      className={clsx(
        'flex min-w-0 flex-col rounded-card border border-line bg-surface shadow-card',
        flow === 'fill' && 'h-full min-h-0',
      )}
      aria-label={t.pos.cart}
    >
      {/* --- header ------------------------------------------------------ */}
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <ShoppingCart className="h-4 w-4 text-butcher-green" aria-hidden="true" />
          {t.pos.cart}
          {readOnly ? <LockKeyhole className="h-3.5 w-3.5 text-ink-muted" aria-label={t.menu.orders.frozenSale} /> : null}
        </h2>

        {!empty ? (
          <span className="numeric rounded-full bg-surface-sunken px-2.5 py-0.5 text-xs font-semibold text-ink-muted">
            {lines.length === 1 ? t.pos.oneItem : format(t.pos.items, { count: lines.length })}
          </span>
        ) : null}
      </header>

      {/* --- lines ------------------------------------------------------- */}
      {/* Scrolls itself in a column; in a drawer the drawer scrolls and this
          just grows, because two nested scrollbars is a thing nobody can
          operate with one thumb. */}
      <div
        className={clsx(
          'min-w-0',
          flow === 'fill' ? 'scrollbar-brand min-h-0 flex-1 overflow-y-auto' : 'shrink-0',
        )}
      >
        {empty ? (
          <EmptyState
            title={t.pos.cartEmpty}
            description={t.pos.cartEmptyHint}
            icon={<ShoppingCart className="h-6 w-6" />}
          />
        ) : (
          <ul className="divide-y divide-line">
            {lines.map((line, index) => {
              const name = locale === 'ar' ? line.nameAr : line.nameEn;

              // A COMBO is one commercial line. Its components stay in the
              // cart as ordinary lines (they are what the server prices,
              // moves and prints), but the screen shows the bundle: one
              // heading with the offer price, the components beneath with
              // no price of their own, and one Remove for the lot.
              if (line.comboGroupKey) {
                const groupLines = lines.filter((entry) => entry.comboGroupKey === line.comboGroupKey);
                if (groupLines[0] !== line) return null;
                const comboName = locale === 'ar' ? line.comboNameAr : line.comboNameEn;
                const groupTotalLbp = groupLines.reduce(
                  (sum, entry) => sum.plus(toDecimal(entry.fixedLineTotalLbp ?? '0')),
                  toDecimal(0),
                );
                const groupUsd = exchangeRate
                  ? groupTotalLbp.dividedBy(toDecimal(exchangeRate)).toDecimalPlaces(2)
                  : toDecimal(0);
                return (
                  <li key={line.comboGroupKey} className="min-w-0 px-4 py-3">
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 flex-1 break-words text-sm font-medium text-ink">
                        <span className="me-1.5 inline-flex items-center gap-1 rounded-full bg-burgundy-red/10 px-1.5 py-0.5 text-[10px] font-semibold text-burgundy-red">
                          <Flame className="h-3 w-3" aria-hidden="true" />
                          {t.offers.comboBadge}
                        </span>
                        {comboName}
                      </p>
                      <span className="numeric shrink-0 text-sm font-bold text-ink">
                        {formatLbp(groupTotalLbp, locale)}
                      </span>
                    </div>
                    <div className="numeric mt-0.5 flex items-baseline justify-between gap-2 text-xs text-ink-muted">
                      <span className="line-through">
                        {line.comboRegularPriceLbp ? formatLbp(line.comboRegularPriceLbp, locale) : ''}
                      </span>
                      <span className="shrink-0">{formatUsd(groupUsd)}</span>
                    </div>
                    <ul className="mt-1 space-y-0.5 ps-3 text-xs text-ink-muted">
                      {groupLines.map((entry) => (
                        <li key={entry.id} className="flex items-baseline gap-1.5">
                          <span aria-hidden="true">•</span>
                          <span className="numeric">
                            {entry.quantityMode === 'WEIGHT_KG'
                              ? formatWeight(entry.quantity)
                              : `${toDecimal(entry.quantity).toFixed(0)} ${unitLabel(entry.sellingUnit, locale)}`}
                          </span>
                          <span>{locale === 'ar' ? entry.nameAr : entry.nameEn}</span>
                          {optionLine(
                            { ar: entry.variantLabelAr, en: entry.variantLabelEn },
                            locale === 'ar' ? entry.variantNameAr : entry.variantNameEn,
                            locale,
                          ) ? (
                            <span className="font-semibold text-butcher-green">
                              {optionLine(
                                { ar: entry.variantLabelAr, en: entry.variantLabelEn },
                                locale === 'ar' ? entry.variantNameAr : entry.variantNameEn,
                                locale,
                              )}
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                    {!readOnly ? (
                      <div className="mt-2 flex gap-1.5">
                        <button
                          type="button"
                          onClick={() => onRemoveLine(line)}
                          className="inline-flex items-center gap-1.5 rounded-control border border-burgundy-red/30 px-2.5 py-1.5 text-xs font-medium text-burgundy-red transition-colors hover:bg-burgundy-red/10"
                          aria-label={`${t.pos.removeLine}: ${comboName ?? ''}`}
                        >
                          <Trash2 className="h-3 w-3" aria-hidden="true" />
                        </button>
                      </div>
                    ) : null}
                  </li>
                );
              }
              void index;
              // The type, on its own line under the product. Operationally
              // this is the important half: "طاووق متبل" alone tells the
              // butcher nothing.
              // "الحجم: كبير" rather than a bare "كبير". Which of two sizes
              // is the operational half of the line, and a butcher reading
              // back an order needs to know what question was answered.
              const chosenOption = optionLine(
                { ar: line.variantLabelAr, en: line.variantLabelEn },
                locale === 'ar' ? line.variantNameAr : line.variantNameEn,
                locale,
              );
              // Derived from the lira line at the header's rate - the same
              // arithmetic the server settles with, so the dollars on this
              // row, the subtotal below and the payment dialog all agree.
              const total = lineTotal(line, exchangeRate);
              // THE LINE, in lira: the price the shop set times what was
              // weighed or counted, rounded once. Never the dollar figure
              // multiplied back.
              const totalLbp = exchangeRate ? lineTotalLbp(line, exchangeRate) : null;

              return (
                <li key={line.id} className="min-w-0 px-4 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="min-w-0 flex-1 break-words text-sm font-medium text-ink">
                      {name}
                      {chosenOption ? (
                        <span className="mt-0.5 block text-xs font-semibold text-butcher-green">
                          {chosenOption}
                        </span>
                      ) : null}
                    </p>
                    {/* The line total, in lira. One number, one line. */}
                    <span className="numeric shrink-0 text-sm font-bold text-ink">
                      {exact(totalLbp) ?? formatUsd(total)}
                    </span>
                  </div>

                  {/* The promotion this line was quoted under: the base it
                      came off, and the percentage - the server's answer,
                      shown, never recalculated here. */}
                  {line.promotionType && line.promotionType !== 'NONE' && line.basePriceLbp && line.unitPriceLbp && toDecimal(line.basePriceLbp).gt(line.unitPriceLbp) ? (
                    <p className="mt-0.5 flex flex-wrap items-baseline gap-2 text-xs" data-testid="sale-line-discount">
                      {toDecimal(line.discountPercent).gt(0) ? <span className="rounded-full bg-burgundy-red/10 px-1.5 py-0.5 font-semibold text-burgundy-red">
                        {format(t.pricing.discountBadge, { percent: toDecimal(line.discountPercent ?? '0').toFixed(0) })}
                      </span> : null}
                      {/* The quoted pair reads left to right whatever the page's
                          direction: base, then the effective figure, then the
                          unit it is per - each «amount LBP» drawn as written. */}
                      <bdi dir="ltr" className="inline-flex flex-wrap items-baseline gap-2" data-testid="sale-line-quote">
                        <bdi dir="ltr" className="text-ink-muted line-through">{formatLbp(line.basePriceLbp, locale)}</bdi>
                        <bdi dir="ltr" className="font-semibold text-ink">{formatLbp(line.unitPriceLbp, locale)}</bdi>
                        {line.quantityMode === 'WEIGHT_KG' ? <bdi dir="ltr" className="text-ink-muted">/ {unitLabel(line.sellingUnit, locale)}</bdi> : null}
                      </bdi>
                    </p>
                  ) : null}

                  {/* The working, in dollars: what was weighed or counted,
                      at what each one costs, and what that comes to. The far
                      side carries the dollar line total, so the row reads as
                      one sum rather than as two facts. */}
                  <div className="numeric mt-0.5 flex items-baseline justify-between gap-2 text-xs text-ink-muted">
                    <span className="min-w-0">
                      {line.quantityMode === 'CUSTOM_AMOUNT' ? (
                        t.units.CUSTOM_AMOUNT
                      ) : line.quantityMode === 'WEIGHT_KG' ? (
                        <>
                          {formatWeight(line.quantity)}
                          <span className="mx-1">×</span>
                          {formatUsd(line.unitPrice)}
                          <span>/{unitLabel(line.sellingUnit, locale)}</span>
                        </>
                      ) : (
                        <>
                          {toDecimal(line.quantity).toFixed(0)} {unitLabel(line.sellingUnit, locale)}
                          <span className="mx-1">×</span>
                          {formatUsd(line.unitPrice)}
                        </>
                      )}
                    </span>

                    <span className="shrink-0">{formatUsd(total)}</span>
                  </div>


                  {line.note ? (
                    <p className="mt-1 flex items-start gap-1.5 rounded bg-surface-soft px-2 py-1 text-xs text-ink-muted">
                      <StickyNote className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                      <span className="min-w-0 break-words">{line.note}</span>
                    </p>
                  ) : null}

                  {!readOnly ? (
                    <div className="mt-2 flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => onEditLine(line)}
                        className="inline-flex items-center gap-1.5 rounded-control border border-line-strong px-2.5 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-surface-sunken"
                      >
                        <Pencil className="h-3 w-3" aria-hidden="true" />
                        {t.pos.editLine}
                      </button>

                      <button
                        type="button"
                        onClick={() => onRemoveLine(line)}
                        className="inline-flex items-center gap-1.5 rounded-control border border-burgundy-red/30 px-2.5 py-1.5 text-xs font-medium text-burgundy-red transition-colors hover:bg-burgundy-red/10"
                        aria-label={`${t.pos.removeLine}: ${name}`}
                      >
                        <Trash2 className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {context}
      </div>

      {/* --- totals ------------------------------------------------------ */}
      {/* `sticky bottom-0` in the drawer: the money and the buttons stay on
          screen while the items scroll past behind them. */}
      <footer
        className={clsx(
          'shrink-0 border-t border-line bg-surface-soft p-4',
          readOnly && 'p-3',
          flow === 'natural' && 'sticky bottom-0 z-10',
        )}
      >
        <label htmlFor="order-note" className="label text-xs">
          {t.pos.orderNote}
        </label>
        {readOnly ? <textarea id="order-note" value={orderNote} readOnly rows={1} className="input mb-2 h-10 resize-none bg-surface-sunken py-2 text-sm" /> : <input
          id="order-note"
          type="text"
          value={orderNote}
          onChange={(event) => onOrderNoteChange(event.target.value)}
          placeholder={t.pos.orderNotePlaceholder}
          maxLength={300}
          readOnly={readOnly}
          className={clsx('input mb-3 py-2 text-sm', readOnly && 'bg-surface-sunken')}
        />}

        {/* A charge for taking the order somewhere, on a sale that is not a
            delivery order. It is NOT a cart line: it moves no stock, has no
            category and reaches no kitchen ticket. It is typed here and added
            to the total, exactly as a delivery order's fee is.

            Always shown in counter mode, at zero, rather than hidden behind a
            disclosure - a cashier who has to go looking for it mid-sale with
            a customer waiting will simply not use it. */}
        {feeEditor ? (
          <div className="mb-2 border-t border-line pt-2">
            <div className="flex items-center justify-between gap-2">
              <label htmlFor="counter-fee" className="label mb-0 text-xs">
                {t.delivery.fee}
                {!feeEditor.confirmation ? <span className="ms-1 font-normal text-ink-muted">{t.pos.feeOptional}</span> : null}
              </label>

              <div className="flex shrink-0 items-center gap-1.5">
                {feeEditor.confirmation ? <LbpMoneyInput id="counter-fee" value={feeEditor.amount} onChange={feeEditor.onAmountChange} showUnit={false} disabled={feeEditor.confirmation.disabled || feeEditor.confirmation.busy} className="w-28 [&>input]:h-9 [&>input]:min-w-0 [&>input]:py-1 [&>input]:text-sm" /> : <input
                  id="counter-fee"
                  type="text"
                  inputMode="decimal"
                  value={feeEditor.amount}
                  onChange={(event) => feeEditor.onAmountChange(event.target.value)}
                  placeholder="0.00"
                  aria-describedby={feeEditor.problem ? 'counter-fee-problem' : undefined}
                  aria-invalid={feeEditor.problem ? true : undefined}
                  className={clsx(
                    'input numeric h-9 w-24 py-1 text-end text-sm',
                    feeEditor.problem && 'border-danger',
                  )}
                />}

                {/* LIRA, AND NOTHING TO CHOOSE. The fee is the one figure the
                    shop quotes a customer out loud - "two hundred thousand to
                    take it up the road" - so it is typed in the currency it
                    was quoted in. The currency switch that used to sit here
                    was one more control to get wrong mid-sale, and a fee
                    typed in the wrong one is money. */}
                <span className="shrink-0 text-xs font-semibold text-ink-muted" dir="ltr">
                  {LBP_LABEL[locale]}
                </span>
              </div>
            </div>
            {feeEditor.confirmation ? <button type="button" className="btn-secondary mt-2 w-full py-1.5 text-xs" disabled={feeEditor.confirmation.disabled || feeEditor.confirmation.busy || !/^\d{1,12}$/.test(feeEditor.amount)} onClick={feeEditor.confirmation.onConfirm}>{t.menu.orders.confirmFee}</button> : null}

            {feeEditor.problem ? (
              <p id="counter-fee-problem" className="mt-1 text-xs text-danger" role="alert">
                {feeEditor.problem}
              </p>
            ) : null}

            {/* One tap to the shop's usual fee, only while the field is
                empty - once anything is typed, what is typed is the fee. */}
            {feeEditor.quickFill && feeEditor.amount.trim() === '' ? (
              <button
                type="button"
                onClick={feeEditor.quickFill.onFill}
                className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-line-strong bg-surface px-2.5 py-1 text-xs font-medium text-ink-muted transition-colors hover:bg-surface-sunken"
              >
                {feeEditor.quickFill.label}
              </button>
            ) : null}
          </div>
        ) : null}

        {/* With a fee there are two different numbers called "total", so both
            are named. Without one, nothing changes.

            The items subtotal carries its dollar figure the way the lines do.
            THE FEE DOES NOT: it was quoted and typed in lira, and echoing it
            in dollars invites the question "which one do I owe?" about the
            one number on this panel a customer was told out loud. */}
        {hasFee || showDeliveryFee ? (
          <>
            <div className="mb-1.5 flex items-baseline justify-between gap-2 text-xs">
              <span className="text-ink-muted">{t.delivery.itemsSubtotal}</span>
              <span className="numeric flex items-baseline gap-1.5 text-ink">
                <span>{exact(itemsSubtotalLbp) ?? formatUsd(itemsSubtotal)}</span>
                {exact(itemsSubtotalLbp) ? (
                  <span className="text-ink-muted">{formatUsd(itemsSubtotal)}</span>
                ) : null}
              </span>
            </div>
            <div className="mb-1.5 flex items-baseline justify-between gap-2 text-xs">
              <span className="text-ink-muted">{t.delivery.fee}</span>
              <span className="numeric text-ink">
                {exchangeRate ? formatLbp(feeLbp, locale) : formatUsd(fee)}
              </span>
            </div>
          </>
        ) : null}

        {/* THE TOTAL. Lira is the amount - the number the cashier says out
            loud and the customer counts out - with the dollar equivalent
            underneath for the owner. The dollar figure covers the WHOLE
            total, fee included, because it is converted from the same
            `subtotal` the lira figure comes from; there is no separate dollar
            fee row and no second piece of arithmetic. With no rate set the
            dollar figure stands in above, because then it is the only total
            there is. */}
        <div className="mb-3 flex items-start justify-between gap-2 border-t border-line pt-2">
          <span className="text-sm font-semibold text-ink">
            {hasFee ? t.delivery.finalTotal : t.pos.subtotal}
          </span>
          <span className="numeric flex flex-col items-end leading-tight">
            <span className="text-xl font-bold text-butcher-green">
              {exact(subtotalLbpExact) ?? formatUsd(subtotal)}
            </span>
            {exact(subtotalLbpExact) ? (
              <span className="text-sm font-semibold text-ink-muted">{formatUsd(subtotal)}</span>
            ) : null}
          </span>
        </div>

        {!exchangeRate ? (
          <p className="mb-3 text-xs text-ink-muted">{t.receipt.noRate}</p>
        ) : null}

        {/* Said out loud rather than left as a greyed-out button somebody
            presses three times wondering what is wrong. */}
        {blockedReason ? (
          <p
            className={clsx('mb-2 text-xs text-antique-gold-deep', !readOnly && 'rounded-control border border-antique-gold/40 bg-antique-gold/10 p-2.5')}
            role="alert"
          >
            {blockedReason}
          </p>
        ) : null}

        <div className="space-y-2">
          {!readOnly || onSetAside ? (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClear}
                disabled={empty || readOnly}
                title={readOnly ? t.menu.orders.frozenSale : undefined}
                className={clsx('btn-secondary flex-1', empty && 'opacity-50')}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                {t.pos.clearCart}
              </button>

              {onHold ? (
                <button
                  type="button"
                  onClick={onHold}
                  disabled={empty}
                  className={clsx('btn-secondary flex-1', empty && 'opacity-50')}
                >
                  <PauseCircle className="h-4 w-4" aria-hidden="true" />
                  {t.held.hold}
                </button>
              ) : null}
              {onSetAside ? <button type="button" onClick={onSetAside} disabled={setAsideDisabled} aria-label={t.menu.orders.leaveSale} title={t.menu.orders.leaveSale} className="btn-secondary flex-1"><PauseCircle className="h-4 w-4" aria-hidden="true" />{t.menu.orders.leaveSaleShort}</button> : null}
            </div>
          ) : null}

          {footerExtra}

          {/* The one control on this screen that must never be missed or
              mis-tapped. Taller than anything else in the footer (56px, above
              the 48px used everywhere else) and in a larger, heavier label,
              because it is pressed hundreds of times a day - often without
              looking, by somebody holding meat in the other hand.

              Height only. It stays inside the same footer and takes no space
              from the subtotal, the fee line or a blocked-checkout warning:
              those are what tell a cashier whether pressing it is right. */}
          <button
            type="button"
            onClick={onCheckout}
            disabled={empty || !canCheckout || Boolean(blockedReason)}
            className="btn-primary h-14 w-full gap-2.5 text-base font-bold"
            title={!canCheckout ? t.payment.shiftRequiredHint : undefined}
          >
            <CreditCard className="h-5 w-5" aria-hidden="true" />
            {checkoutLabel ?? t.payment.title}
          </button>
        </div>
      </footer>
    </section>
  );
}
